import { Box, Checkbox, FormControlLabel, MenuItem, TextField, Typography } from "@mui/material";
import { COLORS } from "assets";
import { bcCategoryChoices, bcCategorySuggest, categoryAllowed } from "incidents/bc";
import { fromDateTimeLocal, toDateTimeLocal } from "incidents/time";
import { BcCategory, Incident, IncidentDocumentation, YesNo, YesNoUnsure } from "incidents/types";
import { fieldSx, SourceTag, usePhrase } from "./common";

interface PatchProps {
  incident: Incident;
  draft: IncidentDocumentation;
  disabled: boolean;
  missing: (field: string) => boolean;
  errorFor: (field: string) => string | undefined;
  onChange: (patch: Partial<IncidentDocumentation>) => void;
}

function RequiredWord() {
  const phrase = usePhrase();
  return (
    <Box component="span" sx={{ color: COLORS.error, fontWeight: 700, fontSize: 13, ml: 1 }}>
      {phrase("Required", "Requis")}
    </Box>
  );
}

function choice(phrase: (en: string, fr: string) => string, includeUnknown = false, includeUnsure = false) {
  const rows = [
    { value: "", label: phrase("Choose", "Choisir") },
    { value: "yes", label: phrase("Yes", "Oui") },
    { value: "no", label: phrase("No", "Non") }
  ];
  if (includeUnknown) rows.push({ value: "unknown", label: phrase("Unknown", "Inconnu") });
  if (includeUnsure) rows.push({ value: "unsure", label: phrase("Unsure", "Incertain") });
  return rows;
}

function SelectField({
  label,
  value,
  options,
  disabled,
  required,
  errorText,
  onChange
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  disabled: boolean;
  required?: boolean;
  errorText?: string;
  onChange: (value: string) => void;
}) {
  const showError = Boolean(errorText);
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" mb={0.5}>
        <Typography sx={{ fontWeight: 600 }}>
          {label}
          {required ? <RequiredWord /> : null}
        </Typography>
        <SourceTag filledBySystem={false} />
      </Box>
      <TextField
        select
        fullWidth
        label={label}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        sx={fieldSx}
        error={showError}
        helperText={errorText}
        inputProps={required ? { "aria-required": true, "aria-invalid": showError || undefined } : undefined}
      >
        {options.map((option) => (
          <MenuItem key={option.value || "blank"} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
}

export function namesAllowed(incident: Incident): boolean {
  return incident.site_jurisdiction?.residentNamesAllowed !== false;
}

export function BcRespondExtras({ incident, draft, disabled, missing, errorFor, onChange }: PatchProps) {
  const phrase = usePhrase();
  return (
    <>
      {draft.witnessed === "yes" ? (
        <TextField
          fullWidth
          label={phrase("Title of the staff who witnessed it", "Titre de la personne qui a vu l'événement")}
          value={draft.witnessed_title || ""}
          disabled={disabled}
          onChange={(event) => onChange({ witnessed_title: event.target.value })}
          sx={fieldSx}
          error={Boolean(errorFor("witnessed_title"))}
          helperText={errorFor("witnessed_title")}
        />
      ) : null}
      <SelectField
        label={phrase(
          "Did a physician or nurse practitioner give emergency care?",
          "Un médecin ou une IPS a-t-il donné des soins d'urgence?"
        )}
        value={draft.emergency_care || ""}
        options={choice(phrase)}
        disabled={disabled}
        required={missing("emergency_care")}
        errorText={errorFor("emergency_care")}
        onChange={(value) => onChange({ emergency_care: value as YesNo | "" })}
      />
      {draft.hospital_transfer === "yes" ? (
        <TextField
          fullWidth
          type="datetime-local"
          label={phrase("Time of the hospital transfer", "Heure du transfert à l'hôpital")}
          value={toDateTimeLocal(draft.hospital_time)}
          disabled={disabled}
          InputLabelProps={{ shrink: true }}
          onChange={(event) => onChange({ hospital_time: fromDateTimeLocal(event.target.value) })}
          sx={fieldSx}
          error={Boolean(errorFor("hospital_time"))}
          helperText={errorFor("hospital_time")}
        />
      ) : null}
      {draft.other_party_kind === "staff" ? (
        <SelectField
          label={phrase(
            "Was the involved staff member removed from this resident's care?",
            "Le membre du personnel concerné a-t-il été retiré des soins de cet usager?"
          )}
          value={draft.staff_removed_from_care || ""}
          options={choice(phrase)}
          disabled={disabled}
          required={missing("staff_removed_from_care")}
        errorText={errorFor("staff_removed_from_care")}
          onChange={(value) => onChange({ staff_removed_from_care: value as YesNo | "" })}
        />
      ) : null}
    </>
  );
}

export function BcAssessExtras({ incident, draft, disabled, missing, errorFor, onChange }: PatchProps) {
  const phrase = usePhrase();
  const behaviour = incident.type === "unsafe_interaction" || incident.type === "responsive_behaviour";
  const protocol = incident.site_jurisdiction?.postFallProtocol;
  const onFile = Boolean(protocol?.name && protocol?.version);
  const schedule = onFile
    ? `${protocol?.name} (${protocol?.version})`
    : phrase(
        "No schedule is on file. A facility admin enters the name and version from this home's policy. Do not invent an interval.",
        "Aucun horaire n'est au dossier. Un administrateur inscrit le nom et la version de la politique du milieu. N'inventez pas d'intervalle."
      );
  return (
    <>
      <Box>
        <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
          {phrase("Vital signs were not taken because", "Signes vitaux non pris parce que")}
          {missing("vitals") ? <RequiredWord /> : null}
        </Typography>
        <TextField
          fullWidth
          label={phrase("Vital signs were not taken because", "Signes vitaux non pris parce que")}
          value={draft.vitals_not_done_reason || ""}
          disabled={disabled}
          onChange={(event) => onChange({ vitals_not_done_reason: event.target.value })}
          sx={fieldSx}
          error={Boolean(errorFor("vitals"))}
          helperText={errorFor("vitals")}
        />
      </Box>
      {incident.type === "fall" ? (
        <>
          <SelectField
            label={phrase(
              "Was the resident's head struck? Choose unknown if nobody saw the fall.",
              "La tête a-t-elle été frappée? Choisissez inconnu si personne n'a vu la chute."
            )}
            value={draft.head_strike || ""}
            options={choice(phrase, true)}
            disabled={disabled}
            required={missing("head_strike")}
        errorText={errorFor("head_strike")}
            onChange={(value) => onChange({ head_strike: value as IncidentDocumentation["head_strike"] })}
          />
          {draft.head_strike === "yes" || draft.head_strike === "unknown" ? (
            <>
              <Typography sx={{ color: COLORS.surface.secondaryText }}>
                {phrase("Post-fall schedule on file for this home:", "Horaire post-chute au dossier pour ce milieu :")}{" "}
                {schedule}
              </Typography>
              <SelectField
                label={phrase("Were neuro checks started?", "Les contrôles neurologiques ont-ils été commencés?")}
                value={draft.neuro_checks_started || ""}
                options={choice(phrase)}
                disabled={disabled}
                required={missing("neuro_checks_started")}
        errorText={errorFor("neuro_checks_started")}
                onChange={(value) => {
                  const started = value as YesNo | "";
                  if (started === "yes" && onFile) {
                    onChange({
                      neuro_checks_started: started,
                      post_fall_protocol_name: protocol?.name || "",
                      post_fall_protocol_version: protocol?.version || ""
                    });
                    return;
                  }
                  onChange({
                    neuro_checks_started: started,
                    post_fall_protocol_name: "",
                    post_fall_protocol_version: ""
                  });
                }}
              />
              {draft.neuro_checks_started === "no" ? (
                <TextField
                  fullWidth
                  label={phrase(
                    "Why neuro checks were not started",
                    "Pourquoi les contrôles neurologiques n'ont pas commencé"
                  )}
                  value={draft.neuro_checks_reason || ""}
                  disabled={disabled}
                  onChange={(event) => onChange({ neuro_checks_reason: event.target.value })}
                  sx={fieldSx}
                  error={Boolean(errorFor("neuro_checks_reason"))}
                  helperText={errorFor("neuro_checks_reason")}
                />
              ) : null}
              {missing("post_fall_protocol") ? (
                <Typography color="error">
                  {phrase(
                    "Use the schedule on file. There is no place to type a different interval.",
                    "Utilisez l'horaire au dossier. Il n'y a pas de champ pour un autre intervalle."
                  )}
                </Typography>
              ) : null}
            </>
          ) : null}
          <SelectField
            label={phrase("Was the environment checked?", "L'environnement a-t-il été vérifié?")}
            value={draft.environment_checked || ""}
            options={choice(phrase)}
            disabled={disabled}
            required={missing("environment_checked")}
        errorText={errorFor("environment_checked")}
            onChange={(value) => onChange({ environment_checked: value as YesNo | "" })}
          />
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
              {phrase(
                "What you checked, or why it was not checked",
                "Ce que vous avez vérifié, ou pourquoi ce n'est pas fait"
              )}
              {missing("environment_note") ? <RequiredWord /> : null}
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label={phrase(
                "What you checked, or why it was not checked",
                "Ce que vous avez vérifié, ou pourquoi ce n'est pas fait"
              )}
              value={draft.environment_note || ""}
              disabled={disabled}
              onChange={(event) => onChange({ environment_note: event.target.value })}
              sx={fieldSx}
              error={Boolean(errorFor("environment_note"))}
              helperText={errorFor("environment_note")}
            />
          </Box>
        </>
      ) : null}
      {behaviour ? (
        <>
          <SelectField
            label={phrase(
              "Who was the other person? The camera does not decide this.",
              "Qui était l'autre personne? La caméra ne le décide pas."
            )}
            value={draft.other_party_kind || ""}
            options={[
              { value: "", label: phrase("Choose", "Choisir") },
              { value: "resident", label: phrase("Another person in care", "Une autre personne hébergée") },
              { value: "staff", label: phrase("Staff", "Personnel") },
              { value: "visitor", label: phrase("Visitor", "Visiteur") },
              { value: "unknown", label: phrase("Unknown, not a resident", "Inconnu, pas un résident") },
              { value: "no_one", label: phrase("No other person", "Aucune autre personne") }
            ]}
            disabled={disabled}
            required={missing("other_party_kind")}
        errorText={errorFor("other_party_kind")}
            onChange={(value) => onChange({ other_party_kind: value as IncidentDocumentation["other_party_kind"] })}
          />
          {draft.other_party_kind === "resident" ? (
            <>
              <TextField
                fullWidth
                label={phrase("Other person's room", "Chambre de l'autre personne")}
                value={draft.other_resident_room || ""}
                disabled={disabled}
                onChange={(event) => onChange({ other_resident_room: event.target.value, other_resident_name: "" })}
                sx={fieldSx}
                helperText={
                  namesAllowed(incident)
                    ? phrase("Room is enough. A name is optional only if this home stores names.", "La chambre suffit.")
                    : phrase(
                        "This home does not store resident names. Enter the room only.",
                        "Ce milieu ne conserve pas les noms. Inscrivez seulement la chambre."
                      )
                }
              />
              {namesAllowed(incident) ? (
                <TextField
                  fullWidth
                  label={phrase(
                    "Other person's name, if this home stores names",
                    "Nom de l'autre personne, si ce milieu conserve les noms"
                  )}
                  value={draft.other_resident_name || ""}
                  disabled={disabled}
                  onChange={(event) => onChange({ other_resident_name: event.target.value })}
                  sx={fieldSx}
                />
              ) : null}
              <SelectField
                label={phrase(
                  "Is this behaviour already in the care plan?",
                  "Ce comportement est-il déjà au plan de soins?"
                )}
                value={draft.in_care_plan || ""}
                options={choice(phrase)}
                disabled={disabled}
                required={missing("in_care_plan")}
        errorText={errorFor("in_care_plan")}
                onChange={(value) => onChange({ in_care_plan: value as YesNo | "" })}
              />
            </>
          ) : null}
          {draft.other_party_kind === "staff" ||
          draft.other_party_kind === "visitor" ||
          draft.other_party_kind === "unknown" ? (
            <SelectField
              label={phrase(
                "Was this done by someone who is not a person in care, or was a care need missed? Unsure stays reportable. The regulatory words are in the category below.",
                "Est-ce fait par quelqu'un qui n'est pas une personne hébergée, ou un besoin de soins a-t-il été manqué? Incertain reste à signaler. Les mots réglementaires sont dans la catégorie plus bas."
              )}
              value={draft.suspected_abuse || ""}
              options={choice(phrase, false, true)}
              disabled={disabled}
              required={missing("suspected_abuse")}
        errorText={errorFor("suspected_abuse")}
              onChange={(value) => onChange({ suspected_abuse: value as YesNoUnsure | "" })}
            />
          ) : null}
          {draft.suspected_abuse === "yes" || draft.suspected_abuse === "unsure" ? (
            <SelectField
              label={phrase("What did you see?", "Qu'avez-vous vu?")}
              value={draft.conduct || ""}
              options={[
                { value: "", label: phrase("Choose", "Choisir") },
                { value: "force", label: phrase("Force", "Force") },
                {
                  value: "verbal",
                  label: phrase("Yelling, harassment, or confinement", "Cris, harcèlement ou confinement")
                },
                { value: "sexual", label: phrase("Sexual behaviour", "Comportement sexuel") },
                {
                  value: "unmet_need",
                  label: phrase("A care need was not met", "Un besoin de soins n'a pas été comblé")
                },
                { value: "financial", label: phrase("Money or property", "Argent ou biens") }
              ]}
              disabled={disabled}
              required={missing("conduct")}
        errorText={errorFor("conduct")}
              onChange={(value) => onChange({ conduct: value as IncidentDocumentation["conduct"] })}
            />
          ) : null}
          <SelectField
            label={phrase("Did an injury need first aid?", "Une blessure a-t-elle eu besoin de premiers soins?")}
            value={draft.first_aid_needed || ""}
            options={choice(phrase)}
            disabled={disabled}
            required={missing("first_aid_needed")}
        errorText={errorFor("first_aid_needed")}
            onChange={(value) => onChange({ first_aid_needed: value as YesNo | "" })}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={phrase("Intervention and how the resident responded", "Intervention et réponse de l'usager")}
            value={draft.intervention || ""}
            disabled={disabled}
            onChange={(event) => onChange({ intervention: event.target.value })}
            sx={fieldSx}
            error={Boolean(errorFor("intervention"))}
            helperText={errorFor("intervention")}
          />
          <TextField
            fullWidth
            label={phrase("Residents who witnessed this, or none", "Résidents témoins, ou aucun")}
            value={draft.witness_residents || ""}
            disabled={disabled}
            onChange={(event) => onChange({ witness_residents: event.target.value })}
            sx={fieldSx}
            error={Boolean(errorFor("witness_residents"))}
            helperText={errorFor("witness_residents")}
          />
          <SelectField
            label={phrase("Was an emergency restraint used?", "Une contention d'urgence a-t-elle été utilisée?")}
            value={draft.restraint_used || ""}
            options={choice(phrase)}
            disabled={disabled}
            required={missing("restraint_used")}
        errorText={errorFor("restraint_used")}
            onChange={(value) => onChange({ restraint_used: value as YesNo | "" })}
          />
          <SelectField
            label={phrase("Is a crime possible?", "Un crime est-il possible?")}
            value={draft.criminal_offence || ""}
            options={choice(phrase, false, true)}
            disabled={disabled}
            required={missing("criminal_offence")}
        errorText={errorFor("criminal_offence")}
            onChange={(value) => onChange({ criminal_offence: value as YesNoUnsure | "" })}
          />
          <SelectField
            label={phrase("Is the clip being held?", "Le clip est-il conservé?")}
            value={draft.evidence_preserved || ""}
            options={choice(phrase)}
            disabled={disabled}
            required={missing("evidence_preserved")}
        errorText={errorFor("evidence_preserved")}
            onChange={(value) => onChange({ evidence_preserved: value as YesNo | "" })}
          />
        </>
      ) : null}
    </>
  );
}

export function BcDocumentExtras({ incident, draft, disabled, missing, errorFor, onChange }: PatchProps) {
  const phrase = usePhrase();
  const behaviour = incident.type === "unsafe_interaction" || incident.type === "responsive_behaviour";
  const suggestion = bcCategorySuggest(incident, draft);
  const choices = bcCategoryChoices().filter((row) =>
    categoryAllowed({ ...incident, documentation: draft }, row.value, draft)
  );
  const showAga =
    behaviour &&
    Boolean(draft.other_party_kind) &&
    draft.other_party_kind !== "resident" &&
    draft.other_party_kind !== "no_one" &&
    (draft.suspected_abuse === "yes" || draft.suspected_abuse === "unsure");
  return (
    <>
      {incident.type !== "unsafe_interaction" ? (
        <TextField
          fullWidth
          multiline
          minRows={2}
          label={phrase(
            "Resident's own account, or that they could not say",
            "Compte de l'usager, ou qu'il n'a pas pu parler"
          )}
          value={draft.resident_statement || ""}
          disabled={disabled}
          onChange={(event) => onChange({ resident_statement: event.target.value })}
          sx={fieldSx}
          error={Boolean(errorFor("resident_statement"))}
          helperText={errorFor("resident_statement")}
        />
      ) : null}
      {incident.type === "fall" ? (
        <TextField
          fullWidth
          multiline
          minRows={2}
          label={phrase("Contributing factors, or none identified", "Facteurs contributifs, ou aucun")}
          value={draft.contributing_factors || ""}
          disabled={disabled}
          onChange={(event) => onChange({ contributing_factors: event.target.value })}
          sx={fieldSx}
          error={Boolean(errorFor("contributing_factors"))}
          helperText={errorFor("contributing_factors")}
        />
      ) : null}
      <TextField
        fullWidth
        multiline
        minRows={2}
        label={phrase(
          "Corrective actions and care-plan changes",
          "Mesures correctives et changements au plan de soins"
        )}
        value={draft.corrective_actions || ""}
        disabled={disabled}
        onChange={(event) => onChange({ corrective_actions: event.target.value })}
        sx={fieldSx}
        error={Boolean(errorFor("corrective_actions"))}
        helperText={errorFor("corrective_actions")}
      />
      <Box sx={{ p: 1.5, bgcolor: "#F4F5F7", borderRadius: 2 }}>
        <Typography sx={{ fontWeight: 700 }}>
          {phrase(
            "Regulatory category for the health authority form",
            "Catégorie réglementaire pour le formulaire de l'autorité"
          )}
        </Typography>
        <Typography sx={{ mt: 0.5 }}>
          {phrase("Product label:", "Libellé du produit :")} {suggestion.product_label}
        </Typography>
        <Typography sx={{ mt: 0.5 }}>{suggestion.mapping}</Typography>
        <Typography sx={{ mt: 0.5, color: COLORS.surface.secondaryText }}>
          {phrase("Suggested regulatory term:", "Terme réglementaire suggéré :")} {suggestion.regulatory_term}.{" "}
          {suggestion.reportable
            ? phrase(
                "This suggestion is reportable. You confirm the category.",
                "Cette suggestion est à signaler. Vous confirmez la catégorie."
              )
            : phrase(
                "This suggestion stays a facility record. It is not sent to licensing.",
                "Cette suggestion reste un dossier de l'établissement. Elle n'est pas envoyée à l'autorité."
              )}
        </Typography>
        {suggestion.reportable || choices.length ? (
          <>
            <TextField
              select
              fullWidth
              sx={{ ...fieldSx, mt: 1 }}
              label={phrase("Regulatory category", "Catégorie réglementaire")}
              value={draft.bc_category || ""}
              disabled={disabled || !choices.length}
              onChange={(event) =>
                onChange({ bc_category: event.target.value as BcCategory, bc_category_confirmed: false })
              }
              error={Boolean(errorFor("bc_category"))}
              helperText={
                errorFor("bc_category") ||
                (missing("bc_category")
                  ? phrase("Required", "Requis")
                  : phrase(
                      "These words appear on the British Columbia export only.",
                      "Ces mots apparaissent seulement sur l'export de la Colombie-Britannique."
                    ))
              }
            >
              <MenuItem value="">{phrase("Choose", "Choisir")}</MenuItem>
              {choices.map((row) => (
                <MenuItem key={row.value} value={row.value}>
                  {row.regulatory}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(draft.bc_category_confirmed)}
                  disabled={disabled || !draft.bc_category}
                  onChange={(_, checked) => onChange({ bc_category_confirmed: checked })}
                />
              }
              label={phrase("I confirm this regulatory category", "Je confirme cette catégorie réglementaire")}
            />
          </>
        ) : null}
        {!suggestion.reportable ? (
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(draft.facility_record_confirmed)}
                disabled={disabled}
                onChange={(_, checked) =>
                  onChange({
                    facility_record_confirmed: checked,
                    bc_category: checked ? "not_reportable" : draft.bc_category
                  })
                }
              />
            }
            label={
              incident.bc_template === "BC_AL"
                ? phrase(
                    "I confirm this stays a facility record. It is not sent to the Assisted Living Registry.",
                    "Je confirme que ceci reste un dossier de l'établissement. Il n'est pas envoyé au registre."
                  )
                : phrase(
                    "I confirm this stays a facility record under Residential Care Regulation s.88. It is not sent to licensing.",
                    "Je confirme que ceci reste un dossier de l'établissement (règlement sur les soins en établissement, art. 88). Il n'est pas envoyé à l'autorité."
                  )
            }
          />
        ) : null}
      </Box>
      {showAga ? (
        <Box sx={{ p: 1.5, border: `1px solid ${COLORS.surface.border}`, borderRadius: 2 }}>
          <Typography sx={{ fontWeight: 700 }}>
            {phrase("Designated agency (optional)", "Organisme désigné (facultatif)")}
          </Typography>
          <Typography sx={{ mt: 0.5 }}>
            {phrase(
              "Adult Guardianship Act s.46 lets someone report when an adult may be abused or neglected and unable to seek help. That report is optional. The licence regulation is the mandatory duty. Do not type the reporter's name.",
              "L'article 46 de l'Adult Guardianship Act permet un signalement facultatif. Le règlement de permis est l'obligation. N'inscrivez pas le nom de la personne qui signale."
            )}
          </Typography>
          {incident.site_jurisdiction?.agaAgencyPhone ? (
            <Typography sx={{ mt: 0.5 }}>
              {phrase("Agency phone on file:", "Téléphone de l'organisme au dossier :")}{" "}
              {incident.site_jurisdiction.agaAgencyPhone}
            </Typography>
          ) : null}
          <SelectField
            label={phrase("Did anyone make that optional report?", "Quelqu'un a-t-il fait ce signalement facultatif?")}
            value={draft.aga_reported || ""}
            options={choice(phrase)}
            disabled={disabled}
            onChange={(value) => onChange({ aga_reported: value as YesNo | "", aga_considered: "yes" })}
          />
          {draft.aga_reported === "yes" ? (
            <>
              <TextField
                fullWidth
                type="datetime-local"
                label={phrase("Date of that report", "Date de ce signalement")}
                value={toDateTimeLocal(draft.aga_reported_at)}
                disabled={disabled}
                InputLabelProps={{ shrink: true }}
                onChange={(event) => onChange({ aga_reported_at: fromDateTimeLocal(event.target.value) })}
                sx={{ ...fieldSx, mt: 1 }}
              />
              <TextField
                fullWidth
                label={phrase("Reference. Not a person's name.", "Référence. Pas un nom de personne.")}
                value={draft.aga_reference || ""}
                disabled={disabled}
                onChange={(event) => onChange({ aga_reference: event.target.value })}
                sx={{ ...fieldSx, mt: 1 }}
                error={Boolean(errorFor("aga_reference"))}
                helperText={errorFor("aga_reference")}
              />
            </>
          ) : null}
        </Box>
      ) : null}
    </>
  );
}

const DESIGNATIONS = [
  { value: "RN", en: "RN", fr: "RN" },
  { value: "LPN", en: "LPN", fr: "LPN" },
  { value: "RPN", en: "RPN — registered psychiatric nurse", fr: "RPN — infirmière psychiatrique autorisée" },
  { value: "NP", en: "NP", fr: "IPS" },
  { value: "HCA", en: "HCA", fr: "HCA" }
];

export function BcReviewExtras({
  incident,
  draft,
  disabled,
  missing,
  errorFor,
  actorName,
  provinceConfirmed,
  onProvinceConfirmed,
  onChange
}: PatchProps & {
  actorName: string;
  provinceConfirmed: boolean;
  onProvinceConfirmed: (value: boolean) => void;
}) {
  const phrase = usePhrase();
  const locale = phrase("en", "fr") === "fr" ? "fr" : "en";
  const setting =
    incident.bc_template === "BC_AL"
      ? phrase("assisted living", "résidence avec assistance")
      : phrase("long-term care", "soins de longue durée");
  return (
    <>
      <Typography>
        {phrase(
          `This home is British Columbia, ${setting}. Confirm that. A facility admin changes the home. You cannot replace the province or the care setting on this record.`,
          `Ce milieu est en Colombie-Britannique, ${setting}. Confirmez-le. Un administrateur modifie le milieu. Vous ne pouvez pas remplacer la province ou le milieu de soins sur ce dossier.`
        )}
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={provinceConfirmed}
            disabled={disabled}
            onChange={(_, checked) => onProvinceConfirmed(checked)}
          />
        }
        label={
          <span>
            {phrase("I confirm this home's province and care setting", "Je confirme la province et le milieu de soins")}
            {missing("province_confirmed") ? <RequiredWord /> : null}
          </span>
        }
      />
      {missing("ai_policy") ? (
        <Typography color="error">
          {phrase(
            "This home has not confirmed an employer policy for AI-assisted documentation. A facility admin records that before this record can be finalized. You remain accountable for what you write.",
            "Ce milieu n'a pas confirmé une politique de l'employeur pour la documentation assistée. Un administrateur l'inscrit avant la finalisation. Vous restez responsable de ce que vous écrivez."
          )}
        </Typography>
      ) : null}
      <Box sx={{ mb: 0.5 }}>
        <Typography component="span" sx={{ fontWeight: 600 }}>
          {phrase("Your designation", "Votre titre")}
          {missing("attestation_designation") ? <RequiredWord /> : null}
        </Typography>
      </Box>
      <TextField
        select
        fullWidth
        label={phrase("Your designation", "Votre titre")}
        value={(draft.attestation_designation || "").toUpperCase()}
        disabled={disabled}
        onChange={(event) => onChange({ attestation_designation: event.target.value, attestation_name: actorName })}
        sx={fieldSx}
        error={Boolean(errorFor("attestation_designation"))}
        helperText={
          errorFor("attestation_designation") ||
          (missing("attestation_designation")
            ? phrase(
                "Required. Choose RN, LPN, RPN, NP, or HCA. PSW is not accepted on this record.",
                "Requis. Choisissez RN, LPN, RPN, NP ou HCA. PSW n'est pas accepté sur ce dossier."
              )
            : phrase("RPN means registered psychiatric nurse.", "RPN signifie infirmière psychiatrique autorisée."))
        }
      >
        <MenuItem value="">{phrase("Choose", "Choisir")}</MenuItem>
        {DESIGNATIONS.map((row) => (
          <MenuItem key={row.value} value={row.value}>
            {locale === "fr" ? row.fr : row.en}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        fullWidth
        label={phrase("Your name, from your account", "Votre nom, depuis votre compte")}
        value={actorName}
        disabled
        sx={fieldSx}
        helperText={phrase(
          "You sign your own entry. The name cannot be changed on this record.",
          "Vous signez votre propre entrée. Le nom ne peut pas être changé sur ce dossier."
        )}
      />
    </>
  );
}
